import type {
  Assumptions,
  ClientPlan,
  ClientListItem,
  Firm,
  TeamMember,
  AdviserProfile,
  IsoDate,
  Scenario,
} from '@cf/shared';
import {
  AdviserRole,
  DEMO_START_YEAR,
  isoDate,
  makeAdviserProfile,
  makeAssumptions,
  makeClientRead,
  makeClientPlan,
  makeDemoPlan,
  makeFirm,
  makeScenario,
  makeLifeEvent,
  makeIncomeStream,
  makeAsset,
  makeSpendingItem,
  pence,
} from '@cf/shared';

/** The injected "today" for the whole fixture world. */
export const SEED_AS_OF: IsoDate = isoDate(`${DEMO_START_YEAR}-04-06`);
export const SEED_START_YEAR = DEMO_START_YEAR;

const CLIENT_1 = '00000000-0000-4000-8000-0000000000c1';
const CLIENT_2 = '00000000-0000-4000-8000-0000000000c2';
const CLIENT_3 = '00000000-0000-4000-8000-0000000000c3';

export interface SeedData {
  firm: Firm;
  adviser: AdviserProfile;
  clients: ClientListItem[];
  /** The firm's advisers — the pool the owner may reassign a client to. */
  teamMembers: TeamMember[];
  plans: Map<string, ClientPlan>;
  assumptions: Map<string, Assumptions>;
  scenarios: Map<string, Scenario[]>;
}

export function buildSeed(): SeedData {
  const firm = makeFirm({
    name: 'Harbourline Financial Planning',
    brandColourHex: '#1f716c',
  });
  const adviser = makeAdviserProfile({
    firmId: firm.id,
    displayName: 'Eleanor Voss',
    email: 'eleanor.voss@harbourline.example.co.uk',
  });

  /**
   * A three-adviser firm, so the Adviser column shows the states that actually
   * occur: a client held by the signed-in principal, one held by a colleague,
   * and one whose holder has left (decision 3 — those pass to the owner, and
   * until they do the list must say the holder is no longer active rather than
   * showing a name that no longer works here).
   */
  const COLLEAGUE = { id: '00000000-0000-4000-8000-0000000000d2', name: 'Marcus Ellery' };
  const DEPARTED = { id: '00000000-0000-4000-8000-0000000000d3', name: 'Priya Raman' };

  // Client 1 — the rich demo case (age 47 at seed start year).
  const client1 = makeClientRead({
    id: CLIENT_1,
    firmId: firm.id,
    displayName: 'Alexandra Marlow',
    dateOfBirth: isoDate('1979-03-14'),
  });
  // Plan progress for the list's chips. The fixture firm is a working book:
  // one client taken all the way to a report, one part-way, one archived after
  // a report. These are workflow FACTS with dates behind them — never a
  // verdict on the plan.
  const client1Progress = {
    factFindSavedAt: '2026-08-24T16:40:00.000Z',
    projectionSavedAt: '2026-08-24T17:05:00.000Z',
    reportExportedAt: '2026-08-25T09:12:00.000Z',
    adviserId: adviser.adviserId,
    adviserName: adviser.displayName,
    adviserActive: true,
    reviewState: 'completed' as const,
    lastReviewLockedAt: '2026-08-25T09:30:00.000Z',
    draftStartedAt: null,
  };
  const plan1 = makeDemoPlan(CLIENT_1);
  const assumptions1 = makeAssumptions();
  const scenarios1: Scenario[] = [
    makeScenario({
      id: '00000000-0000-4000-8000-00000000e5c1',
      clientId: CLIENT_1,
      name: 'Retire at 58',
      description: 'Stops earned income four years earlier; all other assumptions unchanged.',
      assumptionOverrides: { retirementAge: 58 },
      addedLifeEvents: [],
    }),
    makeScenario({
      id: '00000000-0000-4000-8000-00000000e5c2',
      clientId: CLIENT_1,
      name: 'No inheritance',
      description: 'Removes the expected 2035 receipt by offsetting it in full.',
      assumptionOverrides: {},
      addedLifeEvents: [
        makeLifeEvent({
          id: '00000000-0000-4000-8000-00000000ef01',
          clientId: CLIENT_1,
          kind: 'other',
          label: 'Inheritance not received (offset)',
          year: 2035,
          amountPence: pence(150_000_00),
          direction: 'outflow',
          wrapper: 'gia',
          note: 'Scenario counterweight to the plan’s inheritance.',
        }),
      ],
    }),
  ];

  // Client 2 — early-career, sparse plan (age 32).
  const client2 = makeClientRead({
    id: CLIENT_2,
    firmId: firm.id,
    displayName: 'Samir Okafor',
    dateOfBirth: isoDate('1994-11-02'),
  });
  // Part-way: a fact-find captured in the meeting, no projection saved yet.
  const client2Progress = {
    factFindSavedAt: '2026-08-26T11:20:00.000Z',
    projectionSavedAt: null,
    reportExportedAt: null,
    adviserId: COLLEAGUE.id,
    adviserName: COLLEAGUE.name,
    adviserActive: true,
    reviewState: 'draft' as const,
    lastReviewLockedAt: '2025-09-01T10:00:00.000Z',
    draftStartedAt: '2026-09-01T10:00:00.000Z',
  };
  const plan2 = makeClientPlan({
    clientId: CLIENT_2,
    assets: [
      makeAsset({
        id: '00000000-0000-4000-8000-00000000a201',
        clientId: CLIENT_2,
        wrapper: 'cash',
        label: 'Easy-access savings',
        currentAmountPence: pence(9_500_00),
        contributionPence: pence(200_00),
        contributionFrequency: 'monthly',
      }),
      makeAsset({
        id: '00000000-0000-4000-8000-00000000a202',
        clientId: CLIENT_2,
        wrapper: 'dc_pension',
        label: 'Workplace pension',
        currentAmountPence: pence(28_000_00),
        contributionPence: pence(450_00),
        contributionFrequency: 'monthly',
      }),
    ],
    incomeStreams: [
      makeIncomeStream({
        id: '00000000-0000-4000-8000-00000000a203',
        clientId: CLIENT_2,
        kind: 'salary',
        label: 'Salary',
        amountPence: pence(46_000_00),
        frequency: 'annually',
        startYear: null,
        endYear: null,
        endsAtRetirement: true,
      }),
    ],
    spendingItems: [
      makeSpendingItem({
        id: '00000000-0000-4000-8000-00000000a204',
        clientId: CLIENT_2,
        kind: 'essential',
        label: 'Household spending',
        amountPence: pence(1_900_00),
        frequency: 'monthly',
        startYear: null,
        endYear: null,
      }),
    ],
    liabilities: [],
    lifeEvents: [],
  });
  const assumptions2 = makeAssumptions({ retirementAge: 68, horizonYears: 60 });

  // Client 3 — archived record (proves the archive/restore states).
  const client3 = makeClientRead({
    id: CLIENT_3,
    firmId: firm.id,
    displayName: 'Rosemary Whitfield',
    dateOfBirth: isoDate('1958-06-21'),
    status: 'archived',
  });
  const client3Progress = {
    factFindSavedAt: '2026-03-02T10:00:00.000Z',
    projectionSavedAt: '2026-03-02T10:35:00.000Z',
    reportExportedAt: '2026-03-03T14:05:00.000Z',
    adviserId: DEPARTED.id,
    adviserName: DEPARTED.name,
    adviserActive: false,
    reviewState: 'none' as const,
    lastReviewLockedAt: null,
    draftStartedAt: null,
  };
  const plan3 = makeClientPlan({ clientId: CLIENT_3 });
  const assumptions3 = makeAssumptions({ retirementAge: 66, horizonYears: 30 });

  return {
    firm,
    adviser,
    teamMembers: [
      {
        adviserId: adviser.adviserId,
        email: adviser.email,
        displayName: adviser.displayName,
        role: AdviserRole.Owner,
        status: 'active',
        createdAt: '2026-01-01T09:00:00.000Z',
      },
      {
        adviserId: COLLEAGUE.id,
        email: 'marcus.ellery@harbourline.example.co.uk',
        displayName: COLLEAGUE.name,
        role: AdviserRole.Adviser,
        status: 'active',
        createdAt: '2026-01-01T09:00:00.000Z',
      },
      {
        adviserId: DEPARTED.id,
        email: 'priya.raman@harbourline.example.co.uk',
        displayName: DEPARTED.name,
        role: AdviserRole.Adviser,
        // Left the firm. Decision 3 says her clients pass to the owner; until
        // an adviser-deactivation route exists to fire that, the list shows
        // the holder is no longer active so the principal can reassign.
        status: 'deactivated',
        createdAt: '2026-01-01T09:00:00.000Z',
      },
    ],
    clients: [
      { ...client1, ...client1Progress },
      { ...client2, ...client2Progress },
      { ...client3, ...client3Progress },
    ],
    plans: new Map([
      [CLIENT_1, plan1],
      [CLIENT_2, plan2],
      [CLIENT_3, plan3],
    ]),
    assumptions: new Map([
      [CLIENT_1, assumptions1],
      [CLIENT_2, assumptions2],
      [CLIENT_3, assumptions3],
    ]),
    scenarios: new Map([
      [CLIENT_1, scenarios1],
      [CLIENT_2, []],
      [CLIENT_3, []],
    ]),
  };
}
